import { useCallback, useEffect, useState } from "react";
import { acquisitionsApi, type AcqInvoiceDetail } from "@/lib/api/acquisitions";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";

export function useAcqInvoiceDetail(id: string | undefined) {
  const toast = useToast();
  const [invoice, setInvoice] = useState<AcqInvoiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseLoading, setReverseLoading] = useState(false);

  const loadInvoice = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      setInvoice(await acquisitionsApi.acqInvoices.getById(id));
    } catch {
      toast.error("Failed to load invoice.");
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    void loadInvoice();
  }, [loadInvoice]);

  const runAction = async (
    setBusy: (v: boolean) => void,
    action: () => Promise<unknown>,
    successMessage: string,
    fallbackError: string,
    onSuccess?: () => void
  ) => {
    if (!id) return;
    setBusy(true);
    try {
      await action();
      toast.success(successMessage);
      onSuccess?.();
      await loadInvoice();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, fallbackError));
    } finally {
      setBusy(false);
    }
  };

  return {
    invoice,
    loading,
    matching,
    markPaidLoading,
    cancelOpen,
    cancelLoading,
    reverseOpen,
    reverseLoading,
    setCancelOpen,
    setReverseOpen,
    match: () =>
      void runAction(
        setMatching,
        () => acquisitionsApi.acqInvoices.match(id!),
        "Invoice matched — expenditures recorded.",
        "Matching failed."
      ),
    markPaid: () =>
      void runAction(
        setMarkPaidLoading,
        () => acquisitionsApi.acqInvoices.markPaid(id!),
        "Invoice marked as paid.",
        "Could not mark as paid."
      ),
    reverseMatch: () =>
      void runAction(
        setReverseLoading,
        () => acquisitionsApi.acqInvoices.reverseMatch(id!),
        "Match reversed — invoice is Draft again.",
        "Could not reverse match.",
        () => setReverseOpen(false)
      ),
    cancelDraft: () =>
      void runAction(
        setCancelLoading,
        () => acquisitionsApi.acqInvoices.cancelDraft(id!),
        "Invoice cancelled.",
        "Could not cancel invoice.",
        () => setCancelOpen(false)
      ),
  };
}
